import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { insertContactSchema } from "@shared/schema";
import type { Company, User } from "@shared/schema";
import { z } from "zod";
import { useState, useMemo, useEffect } from "react";
import { X, Search } from "lucide-react";

const formSchema = insertContactSchema;
type FormData = z.infer<typeof formSchema>;

interface SelectedCompany {
  companyId: number;
  relationshipType: string;
  isPrimary: boolean;
}

interface CreateContactModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: FormData & { companies: SelectedCompany[] }) => void;
  isLoading: boolean;
  initialData?: { entityType?: string; entityId?: string } | null;
  defaultCompany?: { id: number; name: string };
}

export default function CreateContactModal({
  open,
  onClose,
  onSubmit,
  isLoading,
  defaultCompany,
}: CreateContactModalProps) {
  const [selectedCompanies, setSelectedCompanies] = useState<SelectedCompany[]>([]);

  useEffect(() => {
    if (open && defaultCompany) {
      setSelectedCompanies([{
        companyId: defaultCompany.id,
        relationshipType: "other",
        isPrimary: true,
      }]);
    } else if (!open) {
      setSelectedCompanies([]);
    }
  }, [open, defaultCompany?.id]);
  const [companySearch, setCompanySearch] = useState("");
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

  const { data: companies = [] } = useQuery<Company[]>({
    queryKey: ["/api/companies", { context: "select" }],
    queryFn: async () => {
      const res = await fetch("/api/companies?limit=200", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch companies");
      return res.json();
    },
    enabled: open,
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  // Fuzzy search for companies
  const filteredCompanies = useMemo(() => {
    if (!companySearch.trim()) return companies;
    
    const searchTerm = companySearch.toLowerCase();
    return companies.filter((company: any) =>
      company.name.toLowerCase().includes(searchTerm) ||
      company.type?.toLowerCase().includes(searchTerm)
    );
  }, [companies, companySearch]);

  const { data: users = [] } = useQuery<User[]>({
    queryKey: ["/api/users"],
    enabled: open,
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
  });

  const handleFormSubmit = (data: FormData) => {
    onSubmit({ ...data, companies: selectedCompanies });
    form.reset();
    setSelectedCompanies([]);
    setCompanySearch("");
    setShowCompanyDropdown(false);
  };

  const handleCompanySelect = (company: any) => {
    const isAlreadySelected = selectedCompanies.find(c => c.companyId === company.id);
    if (!isAlreadySelected) {
      setSelectedCompanies(prev => [
        ...prev,
        { companyId: company.id, relationshipType: "other", isPrimary: false }
      ]);
    }
    setCompanySearch("");
    setShowCompanyDropdown(false);
  };

  const removeSelectedCompany = (companyId: number) => {
    setSelectedCompanies(prev => prev.filter(c => c.companyId !== companyId));
  };

  const updateCompanyRelationship = (companyId: number, field: keyof SelectedCompany, value: any) => {
    setSelectedCompanies(prev => 
      prev.map(c => c.companyId === companyId ? { ...c, [field]: value } : c)
    );
  };

  const getCompanyById = (companyId: number) => {
    return companies.find((c: any) => c.id === companyId);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create New Contact</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="john@example.com" type="email" {...field} value={field.value || ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="(555) 123-4567" {...field} value={field.value || ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {/* Company Associations with Search */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Company Associations</label>
                <p className="text-xs text-gray-500">Search and select companies, then configure relationships</p>
              </div>
              
              {/* Company Search */}
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input
                    placeholder="Search companies..."
                    value={companySearch}
                    onChange={(e) => {
                      setCompanySearch(e.target.value);
                      setShowCompanyDropdown(true);
                    }}
                    onFocus={() => setShowCompanyDropdown(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                    className="pl-10"
                  />
                </div>
                
                {/* Search Dropdown */}
                {showCompanyDropdown && companySearch && filteredCompanies.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredCompanies.slice(0, 10).map((company: any) => {
                      const isAlreadySelected = selectedCompanies.find(c => c.companyId === company.id);
                      return (
                        <div
                          key={company.id}
                          className={`px-3 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 ${
                            isAlreadySelected ? 'bg-gray-100 text-gray-500' : ''
                          }`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!isAlreadySelected) handleCompanySelect(company);
                          }}
                        >
                          <div className="font-medium">{company.name}</div>
                          <div className="text-xs text-gray-500">{company.type}</div>
                          {isAlreadySelected && (
                            <div className="text-xs text-blue-600">Already selected</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Selected Companies */}
              {selectedCompanies.length > 0 && (
                <div className="space-y-3">
                  <div className="text-sm font-medium">Selected Companies:</div>
                  {selectedCompanies.map((selectedCompany) => {
                    const company = getCompanyById(selectedCompany.companyId);
                    if (!company) return null;
                    
                    return (
                      <div key={selectedCompany.companyId} className="border rounded-lg p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{company.name}</span>
                            <Badge variant="outline">{company.type}</Badge>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeSelectedCompany(selectedCompany.companyId)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-500">Relationship Type</label>
                            <Select 
                              value={selectedCompany.relationshipType}
                              onValueChange={(value) => updateCompanyRelationship(selectedCompany.companyId, 'relationshipType', value)}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="owner">Owner</SelectItem>
                                <SelectItem value="billing">Billing</SelectItem>
                                <SelectItem value="operations">Operations</SelectItem>
                                <SelectItem value="safety">Safety</SelectItem>
                                <SelectItem value="dispatch">Dispatch</SelectItem>
                                <SelectItem value="compliance">Compliance</SelectItem>
                                <SelectItem value="emergency">Emergency</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center space-x-2 pt-4">
                            <Checkbox
                              id={`primary-${selectedCompany.companyId}`}
                              checked={selectedCompany.isPrimary}
                              onCheckedChange={(checked) => updateCompanyRelationship(selectedCompany.companyId, 'isPrimary', checked)}
                            />
                            <label htmlFor={`primary-${selectedCompany.companyId}`} className="text-xs text-gray-500">
                              Primary Contact
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="jobTitle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Safety Manager" {...field} value={field.value || ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="department"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Department</FormLabel>
                    <FormControl>
                      <Input placeholder="Safety" {...field} value={field.value || ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            
            <FormField
              control={form.control}
              name="userId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Link to App User (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ""}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a user to link (optional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">No user link</SelectItem>
                      {users.map((user: any) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.firstName} {user.lastName} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Creating..." : "Create Contact"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}